/**
 * ProjectImage entity DTOs.
 *
 * Mirrors the Prisma `ProjectImage` model. Surfaces the per-project image
 * library used by the mobile Photos screen (Project → Photos sub-screen,
 * mirrors web at `app/(app)/projects/[projectId]/photos/`).
 *
 * Upload itself goes through the standard mobile presign+commit pipeline
 * (purpose: "project_image"). This module only types the DB rows that the
 * server returns once an image has been committed and registered.
 */
import { z } from "zod";
import { entityIdSchema } from "../common";

export const ProjectImageSchema = z.object({
  id: entityIdSchema,
  projectId: entityIdSchema,
  title: z.string(),
  url: z.string(),
  includeInCpp: z.boolean(),
  extractedFromPdf: z.boolean(),
  createdAt: z.string().datetime(),
});

export type ProjectImage = z.infer<typeof ProjectImageSchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the routes serialise through the Strict form.
export const ProjectImageStrict = ProjectImageSchema.strict();

/**
 * POST /api/mobile/v1/projects/:projectId/images body. The bytes have
 * already been uploaded via the presign flow; this just registers the
 * resulting durable URL + title on the project.
 */
export const ProjectImageCreateRequest = z
  .object({
    title: z.string().min(1).max(200),
    url: z.string().min(1),
    includeInCpp: z.boolean().optional(),
  })
  .strict();

export type ProjectImageCreateRequest = z.infer<
  typeof ProjectImageCreateRequest
>;

/**
 * PATCH /api/mobile/v1/projects/:projectId/images/:imageId body.
 */
export const ProjectImageUpdateRequest = z
  .object({
    title: z.string().min(1).max(200).optional(),
    includeInCpp: z.boolean().optional(),
  })
  .strict();

export type ProjectImageUpdateRequest = z.infer<
  typeof ProjectImageUpdateRequest
>;
